import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    capitalize,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CommonDialog from '../../common/CommonDialog';
import { deleteInventoryApi, getInvcentoryPartDetailAPI } from '../../redux/Inventory/action';
import { INVENTORY_STATUS, INVENTORY_TYPES, PART_TYPES, TableRowsLoader } from '../../utility/Constant';
import PartUsedInvoiceModal from '../Suppliers/SupplierInvoice/PartUsedInvoiceModal';
import AddInventoryModal from './AddInventory';
import { NumericEngine } from '../../utility/helpers/numericEngine';

/**
 * Styles for common UI elements
 */
const styles = {
    // Dialog header styles
    dialogHeader: {
        height: '64px',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },

    // Dialog content styles
    dialogContent: {
        padding: '24px!important',
        maxHeight: '750px',
        overflow: 'auto',
    },

    // Table header cell styles
    tableHeaderCell: {
        padding: '8px 12px',
        height: '40px',
        lineHeight: 'normal',
        fontWeight: 500,
    },

    // Table body cell styles
    tableBodyCell: {
        padding: '7.5px 12px',
        height: '48px !important',
        minHeight: '48px !important',
        maxHeight: '48px !important',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
    },

    // Typography for table cell content
    tableCellTypography: {
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 'normal',
        letterSpacing: '0.17px',
    },

    // Summary box container style
    summaryBoxContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        gap: 1.5,
        borderRadius: '4px',
        marginBottom: 3,
    },

    // Summary box item style with adjusted widths
    summaryBoxItem: {
        padding: '16px',
        borderRight: '1px solid #E0E0E0',
        backgroundColor: '#F3F3F3',
        flex: 1,
        '@media (min-width: 1200px)': {
            '&:first-of-type': {
                flex: 1,
            },
            '&:not(:first-of-type)': {
                flex: 2,
            },
        },
    },

    // Summary item typography style
    summaryItemTypography: {
        fontFamily: 'Roboto',
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '150%',
        letterSpacing: '0.15px',
        color: '#000',
        display: 'flex',
        justifyContent: 'space-between',
        gap: '20px',
    },
};

/**
 * PartDetailsModal - Displays detailed information about a part including its inventory
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the dialog is open
 * @param {Function} props.onClose - Handler function for closing the dialog
 * @returns {React.Component}
 */
export default function PartDetailsModal({ fromCustomerInv = false, saveIndexes, serviceListPayload, open, onClose, inventoryId, fetchAPi }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [isUsedInvoiceQty, setIsUsedInvoiceQty] = useState(null);

    //hooks

    const [disabledFields, setDisabledFields] = useState([]);

    const [partList, setPartList] = useState([]);
    const [isLoader, setLoader] = useState(false);

    const [showLoader, setShowLoader] = useState(false);
    const [batchNumber, setBatchNumber] = useState(null);
    const [inventoryDetail, setInventoryDetail] = useState(null);
    const [openInventoryModel, setOpenInventoryModal] = useState(false);

    const [customerInvoices, setCustomerInvoices] = useState([]);
    const [partInvoiceModal, setPartInvoiceModal] = useState(false);

    //Delete hooks
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Sort parameters for table columns
    const [sortParams, setSortParams] = useState([
        { key: 'supplierInvoice', orderBy: 'DESC' },
        { key: 'qty', orderBy: 'DESC' },
    ]);

    const fetchPartList = useCallback(async () => {
        try {
            setLoader(true);
            const resp = await dispatch(
                getInvcentoryPartDetailAPI({
                    inventory: inventoryId,
                    groupByBatchNumber: true,
                })
            );
            if (resp?.length > 0) {
                if (fromCustomerInv) {
                    const existingBatchArray = serviceListPayload[saveIndexes.outerIndex].parts[saveIndexes.innerIndex]?.batchArray;
                    console.log('existing batchArray:', existingBatchArray);

                    if (existingBatchArray?.length > 0) {
                        // Create a Set of existing batch numbers for efficient lookup
                        const existingBatchNumbers = new Set(existingBatchArray.map((item) => item.batchNumber));

                        // Filter resp to only include items with matching batchNumber
                        const filteredResp = resp.filter((item) => existingBatchNumbers.has(item.batchNumber));

                        // Create a map of existing batch data for quick lookup
                        const batchDataMap = new Map(
                            existingBatchArray.map((item) => [item.batchNumber, { batchCost: item.batchCost, batchQty: item.batchQty }])
                        );

                        // Add batchCost and batchQty from existingBatchArray to filtered items
                        const updatedPartList = filteredResp.map((item) => {
                            const batchData = batchDataMap.get(item.batchNumber);
                            return {
                                ...item,
                                batchCost: batchData?.batchCost || 0,
                                batchQty: batchData?.batchQty || 0,
                            };
                        });
                        console.log('🚀 ~ updatedPartList ~ updatedPartList:', updatedPartList);

                        setPartList(updatedPartList);
                    } else {
                        setPartList([]);
                    }
                } else {
                    setPartList(resp);
                }
            }
        } catch (error) {
            console.log('🚀 ~ fetchPartList ~ error:', error);
        } finally {
            setLoader(false);
        }
    }, [dispatch, inventoryId, fromCustomerInv, serviceListPayload, saveIndexes]);

    useEffect(() => {
        if (open && inventoryId) {
            fetchPartList();
        }
    }, [fetchPartList, open, inventoryId]);

    /**
     * Handles sorting for table columns
     */
    const handleSortRequest = (key) => {
        setSortParams((prevSortParams) => {
            // Find the existing sort index
            const existingSortIndex = prevSortParams.findIndex((param) => param.key === key);

            const newSortParams = [...prevSortParams];

            if (existingSortIndex >= 0) {
                // Toggle the order if the key already exists
                const updatedSort = {
                    ...newSortParams[existingSortIndex],
                    orderBy: newSortParams[existingSortIndex].orderBy === 'ASC' ? 'DESC' : 'ASC',
                };

                // Remove from current position and add to the start
                newSortParams.splice(existingSortIndex, 1);
                newSortParams.unshift(updatedSort);
            } else {
                // Add new sort option to the top
                newSortParams.unshift({ key, orderBy: 'ASC' });
            }

            return newSortParams;
        });
    };

    /**
     * Checks if a column is actively sorted
     * @param {string} key - Column key to check
     * @returns {boolean}
     */
    const isActiveSortColumn = (key) => sortParams.some((param) => param.key === key);

    /**
     * Gets the sort direction for a column
     * @param {string} key - Column key to check
     * @returns {string} - 'asc' or 'desc'
     */
    const getSortDirection = (key) => {
        const param = sortParams.find((param) => param.key === key);
        return param?.orderBy === 'ASC' ? 'asc' : 'desc';
    };

    /**
     * Renders part summary information
     */
    const renderPartSummary = () => (
        <Box sx={styles.summaryBoxContainer}>
            {/* Part Number */}
            <Box
                sx={{
                    ...styles.summaryBoxItem,
                    minWidth: { xs: '48%', lg: 'auto' },
                }}
            >
                <Typography sx={styles.summaryItemTypography}>
                    <span style={{ fontWeight: 500, width: '10px' }}>#</span>{' '}
                    {partList?.length > 0 && (partList[0]?.inventory?.partNumber || partList[0]?.inventory?.tyreModel?.tyreDimension || '-')}
                </Typography>
            </Box>

            {/* Part Name */}
            <Box
                sx={{
                    ...styles.summaryBoxItem,
                    minWidth: { xs: '48%', lg: 'auto' },
                }}
            >
                <Typography sx={styles.summaryItemTypography}>
                    <span style={{ fontWeight: 500, width: '50px' }}>Name</span> {partList?.length > 0 && partList[0]?.inventory?.part?.partName}
                </Typography>
            </Box>

            {/* Part Brand */}
            <Box
                sx={{
                    ...styles.summaryBoxItem,
                    minWidth: { xs: '48%', lg: 'auto' },
                }}
            >
                <Typography sx={styles.summaryItemTypography}>
                    <span style={{ fontWeight: 500, width: '50px' }}>Brand</span> {(partList?.length > 0 && partList[0]?.inventory?.partsBrand?.name) || '-'}
                </Typography>
            </Box>

            {/* Part Category */}
            <Box
                sx={{
                    ...styles.summaryBoxItem,
                    minWidth: { xs: '48%', lg: 'auto' },
                }}
            >
                <Typography sx={styles.summaryItemTypography}>
                    <span style={{ fontWeight: 500, width: '70px' }}>{partList[0]?.inventory?.partsType === PART_TYPES.PARTS ? 'Category' : 'Model'}</span>{' '}
                    {(partList?.length > 0 && (partList[0]?.inventory?.partsCategory?.name || partList[0]?.inventory?.tyreModel?.name)) || '-'}
                </Typography>
            </Box>
        </Box>
    );

    /**
     * Renders table header cells with sort functionality
     * @param {string} label - Column label
     * @param {string} sortKey - Key for sorting
     * @param {boolean} sortable - Whether column is sortable
     * @returns {React.Component}
     */
    const renderTableHeaderCell = (label, minWidth = '', sortKey = null, sortable = false) => (
        <TableCell sx={{ ...styles.tableHeaderCell, minWidth }}>
            {sortable ? (
                <TableSortLabel active={isActiveSortColumn(sortKey)} direction={getSortDirection(sortKey)} onClick={() => handleSortRequest(sortKey)}>
                    {label}
                </TableSortLabel>
            ) : (
                label
            )}
        </TableCell>
    );

    /**
     * Renders a standard table body cell with typography
     * @param {string|number} content - Cell content
     * @returns {React.Component}
     */
    const renderTableBodyCell = (content) => (
        <TableCell sx={styles.tableBodyCell}>
            <Typography sx={styles.tableCellTypography}>{content}</Typography>
        </TableCell>
    );

    return (
        <>
            <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth className="commonDialog__1100">
                {/* Dialog Header */}
                <DialogTitle sx={styles.dialogHeader}>
                    <Typography variant="h6">Part Details</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon style={{ fill: 'black', fontSize: 32 }} />
                    </IconButton>
                </DialogTitle>

                {/* Dialog Content */}
                <DialogContent sx={styles.dialogContent}>
                    {/* Part Summary Information */}
                    {renderPartSummary()}

                    {/* Part Inventory Table */}
                    <TableContainer component={Paper} sx={{ mt: 2, overflow: 'auto', boxShadow: 'none' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ height: '40px' }}>
                                    {partList?.length > 0 && partList[0]?.isTracked && renderTableHeaderCell('Supplier')}
                                    {partList?.length > 0 && partList[0]?.isTracked && renderTableHeaderCell('Supplier Invoice #', 'supplierInvoice', true)}
                                    {renderTableHeaderCell('In Stock', 'qty', true)}
                                    {renderTableHeaderCell('Used Quantity', 165)}
                                    {renderTableHeaderCell('Cost')}
                                    {renderTableHeaderCell('Tracked')}
                                    <TableCell align="right" sx={styles.tableHeaderCell} />
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {isLoader ? (
                                    <TableRowsLoader rowsNum={10} />
                                ) : partList && partList.length > 0 ? (
                                    partList.map((row, index) => (
                                        <TableRow
                                            key={row._id}
                                            sx={{ height: '48px', cursor: 'pointer' }}
                                            hover={true}
                                            onClick={() => {
                                                setBatchNumber(row?.batchNumber);
                                                if (fromCustomerInv) {
                                                    setDisabledFields(['partName', 'partNumber', 'supplierId', 'supplierInvoiceNumber']);
                                                }
                                                setOpenInventoryModal(true);
                                            }}
                                        >
                                            {/* Supplier - Since it's not directly in the API response, we'll leave it blank or use a placeholder */}
                                            {partList?.length > 0 && partList[0]?.isTracked && renderTableBodyCell(row.supplierInvoice?.supplier?.name || '-')}

                                            {/* Supplier Invoice Number */}
                                            {partList?.length > 0 && partList[0]?.isTracked && renderTableBodyCell(row.supplierInvoice?.invoiceNumber || '-')}

                                            {/* Quantity - Using serialNumber as quantity indicator */}
                                            {renderTableBodyCell(row.inStock || 0)}

                                            {/* Used Quantity - Not directly available in API, can calculate based on isAvailable */}
                                            {fromCustomerInv ? renderTableBodyCell(row.batchQty || 0) : renderTableBodyCell(row.sold || 0)}

                                            {/* Cost */}
                                            {renderTableBodyCell(NumericEngine.format(row?.cost || 0, { formatCurrency: true, trailingZeros: true }))}

                                            {/* Tracked status */}
                                            <TableCell sx={styles.tableBodyCell}>
                                                <Chip
                                                    label={row?.isTracked ? capitalize(INVENTORY_STATUS.TRACKED) : capitalize(INVENTORY_STATUS.UNTRACKED)}
                                                    style={{
                                                        background: row.isTracked ? INVENTORY_STATUS.COLORS.TRACKED : INVENTORY_STATUS.COLORS.UNTRACKED,
                                                        color: '#000',
                                                        height: '32px',
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Action buttons */}
                                            <TableCell align="right" sx={styles.tableBodyCell}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => {
                                                        if (fromCustomerInv) {
                                                            setDisabledFields(['partName', 'partNumber', 'supplierId', 'supplierInvoiceNumber']);
                                                        }
                                                        setIsUsedInvoiceQty(row?.batchQty);
                                                        setBatchNumber(row?.batchNumber);
                                                        setOpenInventoryModal(true);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" style={{ color: '#646466' }} />
                                                </IconButton>
                                                <IconButton
                                                    color="error"
                                                    size="small"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setBatchNumber(row?.batchNumber);
                                                        setInventoryDetail(row);
                                                        setShowDeleteModal(true);

                                                        setCustomerInvoices(row?.customerInvoices);
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography sx={styles.tableCellTypography}>No Data</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>

            {openInventoryModel && (
                <AddInventoryModal
                    type={INVENTORY_TYPES.INVENTORY}
                    showUsedInInvoiceFeild={fromCustomerInv ? true : false}
                    FeildLabelName={fromCustomerInv ? 'Added to Invoice' : 'Used In Invoices'}
                    isUsedInvoiceQty={isUsedInvoiceQty}
                    setIsUsedInvoiceQty={setIsUsedInvoiceQty}
                    disabledFields={disabledFields}
                    inventoryId={inventoryId}
                    batchNumber={batchNumber}
                    openModal={openInventoryModel}
                    closeModal={() => {
                        setDisabledFields([]);
                        setOpenInventoryModal(false);
                        setBatchNumber(null);
                    }}
                    onSubmitModal={() => {
                        fetchPartList();
                        setDisabledFields([]);
                        setOpenInventoryModal(false);
                        setBatchNumber(null);
                    }}
                    hideTabs
                    setShowLoader={setShowLoader}
                    showLoader={showLoader}
                    fetchApi={(data) => {
                        if (fromCustomerInv) {
                            fetchAPi(data, false, batchNumber, Number(isUsedInvoiceQty));
                            setDisabledFields([]);
                            onClose();
                        }
                        if (data) {
                            fetchPartList();
                        }
                    }}
                    ShowAllTabs={false}
                    ShowTabIndex={0}
                    modalType="add"
                />
            )}

            <PartUsedInvoiceModal
                open={partInvoiceModal}
                batchNumber={batchNumber}
                onClose={() => {
                    setPartInvoiceModal(false);
                }}
            />

            <CommonDialog
                loader={isDeleting}
                open={showDeleteModal}
                onClose={() => {
                    setInventoryDetail(null);
                    setBatchNumber(null);
                    setShowDeleteModal(false);
                }}
                onSave={async () => {
                    let resp;

                    if (inventoryDetail?.isUsedInCustomerInvoice) {
                        console.log('sdfsdfds', inventoryDetail);
                        setPartInvoiceModal(true);
                        return;
                    }
                    setIsDeleting(true);
                    if (!inventoryDetail?.isTracked) {
                        resp = await deleteInventoryApi(inventoryDetail?.inventory?._id, inventoryDetail?.batchNumber);
                    } else if (inventoryDetail?.supplierInvoice?._id) {
                        navigate(
                            `/suppliers-invoice/${
                                inventoryDetail?.supplierInvoice?.paymentStatus === 'open'
                                    ? 'open'
                                    : inventoryDetail?.supplierInvoice?.paymentStatus === 'partial_payment'
                                      ? 'partial'
                                      : 'paid'
                            }/` + inventoryDetail.supplierInvoice._id
                        );
                    }
                    if (resp) {
                        setInventoryDetail(null);
                        setBatchNumber(null);
                        setShowDeleteModal(false);
                        fetchPartList();
                    }
                    setIsDeleting(false);
                }}
                dialogClaassName={inventoryDetail?.isTracked ? 'commonDialog commonDialog__600' : 'commonDialog'}
                dialogTitle={
                    inventoryDetail?.isUsedInCustomerInvoice
                        ? '🚫 Cannot Delete Inventory'
                        : !inventoryDetail?.isTracked
                          ? 'Delete Part?'
                          : '⚠️ Part added from Supplier Invoice'
                }
                dialogSubtitle={
                    inventoryDetail?.isUsedInCustomerInvoice
                        ? 'This part has been used in at least one customer invoice.'
                        : !inventoryDetail?.isTracked
                          ? 'Are you sure you want to delete this part from your inventory?'
                          : 'This part was originally added through a Supplier Invoice.'
                }
                dialogSubtitle2={
                    inventoryDetail?.isUsedInCustomerInvoice
                        ? 'To preserve invoice history, deletion is not allowed. To hide this part, click View In-Stock Parts Only.'
                        : !inventoryDetail?.isTracked
                          ? ''
                          : 'To maintain accurate inventory and costing, please delete it directly from the Supplier Invoice.'
                }
                saveButtonText={
                    inventoryDetail?.isUsedInCustomerInvoice
                        ? `view Invoice${customerInvoices.length > 1 ? 's' : ''}`
                        : !inventoryDetail?.isTracked
                          ? 'Delete'
                          : 'view supplier Invoice'
                }
                saveButtonType={inventoryDetail?.isUsedInCustomerInvoice ? 'primary' : !inventoryDetail?.isTracked ? 'error' : 'primary'}
                saveButtonWidth={inventoryDetail?.isUsedInCustomerInvoice ? '148px' : !inventoryDetail?.isTracked ? '103px' : '210px'}
            />
        </>
    );
}
